import React, { useState } from 'react';
import notificationService from '../../services/notificationService';
import './NotificationsPanel.css';

/**
 * ONE MICROSOFT TEAMS WEBHOOK, VERIFIED BEFORE IT IS KEPT — the shared field behind every place a Teams channel is
 * named for alerts (the NotificationsPanel of Payment Porter and Sync, and the product Notifications tab of a hotel,
 * chain or partner).
 *
 * Paste the URL → "Verify to complete setup" posts a 6-digit code to the channel → the code typed back proves the URL reaches a channel
 * the person can read. Only then is it committed.
 *
 * Props:
 *   value              the committed URL, or null
 *   onCommit(url|null) keep a verified URL (or remove it). May be async; an error it throws is shown.
 *   verify(url, code)  OPTIONAL — check the code AND keep the URL in one call (a server that verifies on save). Without
 *                      it, the code is checked here (verifyTeamsCode) and onCommit is called.
 *   disabled           read-only: the committed URL is shown, nothing can change
 *   showHelp           the "How to create a Teams webhook" steps (default true)
 *   service            injectable for tests
 */
const TeamsWebhookField = ({ value, onCommit, verify = null, disabled = false, showHelp = true, service = notificationService }) => {
    const [input, setInput] = useState('');
    const [state, setState] = useState({ codeSent: false, code: '', sending: false, verifying: false, error: null });
    const errorOf = (err, fallback) => err.response?.data?.error?.message || err.response?.data?.message || err.message || fallback;

    const sendCode = async () => {
        setState(s => ({ ...s, sending: true, error: null }));
        try {
            await service.sendTeamsCode(input.trim());
            setState(s => ({ ...s, codeSent: true, sending: false }));
        } catch (err) {
            setState(s => ({ ...s, sending: false, error: errorOf(err, 'Failed to send code') }));
        }
    };

    const verifyCode = async () => {
        setState(s => ({ ...s, verifying: true, error: null }));
        try {
            if (verify) {
                await verify(input.trim(), state.code);
            } else {
                await service.verifyTeamsCode(input.trim(), state.code);
                await onCommit(input.trim());
            }
            setState({ codeSent: false, code: '', sending: false, verifying: false, error: null });
            setInput('');
        } catch (err) {
            setState(s => ({ ...s, verifying: false, error: errorOf(err, 'Invalid code') }));
        }
    };

    const remove = async () => {
        try { await onCommit(null); } catch (err) { setState(s => ({ ...s, error: errorOf(err, 'Could not remove it') })); }
    };

    return (
        <>
            {showHelp && !value && !disabled && (
                <details className="notif-teams-help">
                    <summary>How to create a Teams Incoming Webhook</summary>
                    <ol>
                        <li>Open the Microsoft Teams channel that should receive notifications.</li>
                        <li>Click the <strong>•••</strong> menu next to the channel name and choose <strong>Workflows</strong>.</li>
                        <li>Search for <strong>Post to a channel when a webhook request is received</strong> and select it.</li>
                        <li>Name the workflow, pick the team and channel, and click <strong>Add workflow</strong>.</li>
                        <li>Copy the generated webhook URL and paste it below.</li>
                    </ol>
                    <p className="notif-teams-help-note">Keep the URL private — anyone with it can post to the channel.</p>
                </details>
            )}
            {!value ? (
                disabled ? <div className="notif-label-hint">None</div> : (
                    <>
                        <div className="notif-custom-row" style={{ marginTop: '0.375rem' }}>
                            <input
                                className="notif-input notif-email-input"
                                type="url"
                                placeholder="https://…powerplatform.com/… or https://outlook.office.com/webhook/…"
                                value={input}
                                disabled={state.codeSent}
                                onChange={e => { setInput(e.target.value); setState(s => ({ ...s, error: null })); }}
                            />
                            {!state.codeSent ? (
                                <button type="button" className="notif-send-btn" disabled={!input.trim() || state.sending} onClick={sendCode}>
                                    {state.sending ? 'Sending…' : 'Verify to complete setup'}
                                </button>
                            ) : (
                                <button type="button" className="notif-resend-btn"
                                    onClick={() => setState({ codeSent: false, code: '', sending: false, verifying: false, error: null })}>Change</button>
                            )}
                        </div>
                        {state.codeSent && (
                            <div className="notif-custom-row" style={{ marginTop: '0.5rem' }}>
                                <input
                                    className="notif-input notif-code-input"
                                    type="text"
                                    placeholder="6-digit code from Teams"
                                    maxLength={6}
                                    value={state.code}
                                    onChange={e => setState(s => ({ ...s, code: e.target.value.replace(/\D/g, ''), error: null }))}
                                />
                                <button type="button" className="notif-verify-btn" disabled={state.code.length !== 6 || state.verifying} onClick={verifyCode}>
                                    {state.verifying ? 'Verifying…' : 'Verify'}
                                </button>
                            </div>
                        )}
                    </>
                )
            ) : (
                <div className="notif-verified-row" style={{ marginTop: '0.375rem' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#16a34a', flexShrink: 0 }}>
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="notif-email-text" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
                    <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>Verified</span>
                    {!disabled && <button type="button" className="notif-remove-btn" title="Remove this webhook" onClick={remove}>✕</button>}
                </div>
            )}
            {state.error && <div className="notif-error-msg" style={{ marginTop: '0.25rem' }}>{state.error}</div>}
        </>
    );
};

export default TeamsWebhookField;
